/**
 * Shared tokens and helpers for the planogram order tool.
 */

import { PlanogramForm, PlanogramProduct, PlanogramSlot } from '../../@types/planogram';
import { ThemeColors } from '../../styles/ThemeStyles';

/**
 * Facings are colour-coded by product form so a shelf reads at a glance even at
 * the smallest rack zoom, where names are too small to scan.
 */
export const FORM_COLORS: Record<PlanogramForm, string> = {
  Vegetable: '#4C9A3A',
  Flower: '#C2185B',
  Heritage: '#B26B2E',
  Livingston: '#1F4B99',
  Accessory: '#78909C',
};

export const FORM_LABELS: Record<PlanogramForm, string> = {
  Vegetable: 'Vegetable',
  Flower: 'Flower',
  Heritage: 'Heritage',
  Livingston: 'Livingston',
  Accessory: 'Accessory',
};

export type SlotState = 'ok' | 'oos' | 'missing';

export function slotState(product: PlanogramProduct): SlotState {
  if (product.missing) return 'missing';
  if (product.outOfStock) return 'oos';
  return 'ok';
}

export const STATE_COLORS: Record<SlotState, string> = {
  ok: ThemeColors.primary.main,
  oos: ThemeColors.error.main,
  missing: ThemeColors.gray[500],
};

export type PlanogramFilter = 'all' | 'ordered' | 'oos' | 'missing';

export const FILTER_LABELS: Record<PlanogramFilter, string> = {
  all: 'All facings',
  ordered: 'In my order',
  oos: 'Out of stock',
  missing: 'No portal SKU',
};

export function formatPrice(price: number | null): string {
  return typeof price === 'number' ? `$${price.toFixed(2)}` : '—';
}

/** Human-readable slot position, e.g. "Shelf 3 · Facing 2". */
export function positionLabel(slot: Pick<PlanogramSlot, 'row' | 'col'>): string {
  return `Shelf ${slot.row + 1} · Facing ${slot.col + 1}`;
}

/** Compact position badge, e.g. "S3·F2". */
export function positionBadge(slot: Pick<PlanogramSlot, 'row' | 'col'>): string {
  return `S${slot.row + 1}\u00B7F${slot.col + 1}`;
}

/** Matches a product against a free-text query across every code and name. */
export function matchesQuery(product: PlanogramProduct, query: string): boolean {
  if (!query) return true;
  const haystack = [
    product.name,
    product.rackName,
    product.rackCode,
    product.itemCode,
    product.nameFr,
    product.categoryEn,
    product.subCategoryEn,
    FORM_LABELS[product.form],
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

export function passesFilter(
  product: PlanogramProduct,
  filter: PlanogramFilter,
  qty: number
): boolean {
  switch (filter) {
    case 'ordered':
      return qty > 0;
    case 'oos':
      return product.outOfStock;
    case 'missing':
      return product.missing;
    default:
      return true;
  }
}

/** Short buzz on selection. Silently ignored where the API is unavailable. */
export function tapFeedback(): void {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(8);
  }
}
